import { logger } from './logger';
import type {
  Account,
  ResearchResult,
  AccountActivity,
  Deal,
} from '@territory/shared';

const API_BASE = import.meta.env.VITE_USE_PROXY === 'true'
  ? '/api'
  : `${import.meta.env.VITE_BACKEND_URL || ''}/api`;

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public statusText: string,
    public requestId?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface RequestOptions {
  body?: unknown;
  headers?: Record<string, string>;
  formData?: FormData;
}

// --- Wedge standalone token bridge ------------------------------------------
// iOS standalone PWAs lose the httpOnly auth cookies across relaunch, so
// /auth/refresh 401-loops and the rep gets logged out mid-field. The backend
// already accepts the refresh token in the request body
// (`req.cookies?.refresh_token || dto.refreshToken`) and returns rotated
// tokens in the response body. So the wedge bootstraps body tokens ONCE (at
// the OAuth callback, while the cookie is freshest) and never depends on
// cookie persistence again. STRICTLY GATED on these localStorage keys, which
// ONLY the wedge callback writes - the CRM never sets them, so every helper
// below no-ops for the CRM and its cookie-auth behavior is byte-identical.
const RT_KEY = 'rv_wedge_rt';
const AT_KEY = 'rv_wedge_at';
function lsGet(k: string): string | null {
  try { return localStorage.getItem(k); } catch { return null; }
}
export function setWedgeTokens(accessToken?: string, refreshToken?: string): void {
  try {
    if (accessToken) localStorage.setItem(AT_KEY, accessToken);
    if (refreshToken) localStorage.setItem(RT_KEY, refreshToken);
  } catch { /* storage unavailable */ }
  /* 07-27 remediation audit · deliberately does NOT clear
     rv_auth_user_cache. This helper also runs on EVERY token rotation
     (refreshAttempt captures rotated body tokens q15min), so clearing here
     wiped the offline session cache constantly and offline restore almost
     never had a cache to restore from. Identity swaps are handled at the
     actual sign-in entry points instead (PinLogin, native Apple flow,
     magic/code redeem success), which clear the cache explicitly before
     the new identity's tokens land. */
}
export function clearWedgeTokens(): void {
  try { localStorage.removeItem(AT_KEY); localStorage.removeItem(RT_KEY); } catch { /* noop */ }
  /* Deliberately does NOT clear rv_auth_user_cache: this helper also runs as
     a mid-session heal (stale stored refresh token → cookie fallback), and
     wiping the offline cache there silently disabled offline restore for the
     rest of the session. Sign-out clears the cache in AuthContext.logout();
     sign-in swaps clear it at the sign-in entry points (PinLogin, native
     Apple flow, magic/code redeem success). */
}
function wedgeAuthHeader(): Record<string, string> {
  const at = lsGet(AT_KEY);
  return at ? { Authorization: `Bearer ${at}` } : {};
}
// The backend refresh token is SINGLE-USE: presenting a spent token (or the
// same token twice) triggers reuse-detection and revokes the entire family
// (hard logout). Two invariants make this safe:
//  1. GLOBAL single-flight (sharedRefresh) - concurrent 401s across BOTH
//     HttpClient and fetchWithAuth coalesce into ONE refresh / one rotation.
//  2. When the wedge bridge is active (a stored refresh token exists) we send
//     `credentials:'omit'` so a stale auth COOKIE can't present a competing
//     token: the stored body token is the SOLE source of truth, rotated
//     deterministically. (CRM has no stored token → 'include' + {} → byte-
//     identical legacy behavior.)
// Once the wedge bootstrap runs, this client IS a token-bridge client for
// the session: capture+store the rotated body tokens on EVERY refresh
// (incl. the very first cookie-based one). The CRM never calls
// bootstrapWedgeAuth → stays false → never stores → byte-identical.
let _wedgeBootstrap = false;
async function refreshAttempt(rt: string | null, capture: boolean): Promise<number> {
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    // Cookie path when no rt: the httpOnly refresh cookie is the credential.
    credentials: rt ? 'omit' : 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(rt ? { refreshToken: rt } : {}),
  });
  if (res.ok && capture) {
    try {
      const data = await res.clone().json();
      if (data && (data.accessToken || data.refreshToken)) {
        setWedgeTokens(data.accessToken, data.refreshToken);
      }
    } catch { /* non-JSON; cookie path still refreshed fine */ }
  }
  return res.status;
}
async function postRefresh(): Promise<boolean> {
  const rt = lsGet(RT_KEY);
  try {
    const status = await refreshAttempt(rt, !!rt || _wedgeBootstrap);
    if (status >= 200 && status < 300) return true;
    // 2026-07-10 (Monster demo idle-401) · the stored body token can be
    // stale: a prior session's rt left in localStorage (the redeem flows
    // used to bootstrap BEFORE clearing device state) or one spent by a
    // sibling tab. It then 401s here with credentials:'omit', so the
    // perfectly valid refresh COOKIE the login just set never got a
    // chance and the rep's session died at access-token expiry (~15min)
    // with stale-UI + silent save failures. On a definitive auth
    // rejection of the rt path, drop the bad tokens and fall back to the
    // cookie path ONCE (different token family, so no double-spend).
    // Keep capturing body tokens so an active bridge session stays a
    // bridge session after the heal.
    if (rt && (status === 401 || status === 403)) {
      clearWedgeTokens();
      const retryStatus = await refreshAttempt(null, true);
      return retryStatus >= 200 && retryStatus < 300;
    }
    return false;
  } catch {
    return false;
  }
}

// Module-global single-flight. EVERY refresh in the app - HttpClient,
// fetchWithAuth, AND the wedge login bootstrap - funnels through this one
// in-flight promise, so the single-use refresh token is spent exactly once
// per burst. The bootstrap previously did its OWN raw /auth/refresh, racing
// the app's first refresh on the same cookie token → reuse-detection
// revoked the family at login (the "deep scrape never runs / stub" bug).
//
// CROSS-TAB serialization (Web Locks). The in-memory _refreshShared above is
// only single-flight WITHIN this tab. Two tabs on the same origin each have
// their own module instance, so both could hit 401 in parallel and both
// present the SAME single-use RT v1 to the backend → backend reuse-detection
// revokes the whole session family (the "all sessions revoked" incident
// caught in prod 2026-05-22T19:55:39). navigator.locks.request serializes
// refreshes across tabs: when this tab acquires the lock, postRefresh
// re-reads RT from localStorage (which the OTHER tab will have just
// rotated), so the second tab presents RT v2 and never trips reuse.
// Falls back to the in-memory single-flight only when navigator.locks is
// unavailable (very old Safari) - byte-identical to the prior behaviour.
let _refreshShared: Promise<boolean> | null = null;
interface LocksLike {
  request<T>(name: string, callback: () => Promise<T> | T): Promise<T>;
}
function sharedRefresh(): Promise<boolean> {
  if (_refreshShared) return _refreshShared;
  const locks: LocksLike | undefined =
    typeof navigator !== 'undefined'
      ? (navigator as unknown as { locks?: LocksLike }).locks
      : undefined;
  _refreshShared = (
    locks
      ? locks.request('rv-wedge-auth-refresh', () => postRefresh())
      : postRefresh()
  ).finally(() => {
    _refreshShared = null;
  });
  return _refreshShared;
}

// Wedge login bootstrap: capture body tokens while the OAuth cookie is
// freshest, via the SAME single-flight so it can never race the app's first
// refresh. Idempotent; resilient (never throws).
export function bootstrapWedgeAuth(): Promise<boolean> {
  _wedgeBootstrap = true;
  return sharedRefresh();
}

// Public hook into the module-global single-flight refresh, for callers that
// bypass fetchWithAuth/HttpClient (e.g. streaming SSE endpoints that read the
// response body directly). Funnels through the SAME _refreshShared promise so
// the single-use refresh token can never be presented twice in parallel.
// Returns true if the refresh succeeded (a fresh access token cookie + body
// tokens are now in place).
export function refreshAccessToken(): Promise<boolean> {
  return sharedRefresh();
}

// Proactive pre-flight refresh for the research money path. If the stored wedge
// access token is within `withinMs` of expiry, refresh it once (single-flighted
// via sharedRefresh) BEFORE a brief kicks off, so a long brief starts on a fresh
// token instead of eating a reactive 401 round-trip. Gated tight on near-expiry:
// the token would 401-and-refresh inside that same window anyway, so this adds
// no extra single-use-refresh-token rotation. Best-effort: any decode/refresh
// error returns quietly so the caller proceeds to the proven reactive-401 path
// and a brief that would run today can never be blocked here.
export async function ensureFreshWedgeToken(withinMs = 60_000): Promise<void> {
  try {
    const at = lsGet(AT_KEY);
    if (!at) return;
    const parts = at.split('.');
    if (parts.length !== 3) return;
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: number };
    const expMs = typeof payload.exp === 'number' ? payload.exp * 1000 : 0;
    if (expMs > 0 && expMs - Date.now() <= withinMs) {
      await sharedRefresh();
    }
  } catch {
    /* best-effort · the reactive 401 path still covers an expired token */
  }
}

class HttpClient {
  private getHeaders(includeContentType = true): Record<string, string> {
    const headers: Record<string, string> = {
      'X-Request-Id': createRequestId(),
    };
    if (includeContentType) headers['Content-Type'] = 'application/json';
    return headers;
  }

  private buildFetchOptions(method: HttpMethod, options: RequestOptions): RequestInit {
    const fetchOptions: RequestInit = {
      method,
      credentials: 'include',
      // Authed JSON API responses must never be HTTP-cached by the browser.
      // Without this the browser revalidates on reload, the server replies
      // 304, and fetch's Response.ok is false for 304 -> the error branch
      // below throws a bogus ApiError on every cached GET (manifested as the
      // "Connection Error / Invalid user context" screen + stuck loader).
      cache: 'no-store',
      headers: options.formData
        ? { ...this.getHeaders(false), ...wedgeAuthHeader(), ...options.headers }
        : { ...this.getHeaders(), ...wedgeAuthHeader(), ...options.headers },
    };

    if (options.body && !options.formData) {
      fetchOptions.body = JSON.stringify(options.body);
    } else if (options.formData) {
      fetchOptions.body = options.formData;
    }

    return fetchOptions;
  }

  private async tryRefresh(): Promise<boolean> {
    // Delegate to the module-global single-flight so an HttpClient refresh
    // and a fetchWithAuth refresh can never run concurrently with the same
    // single-use token (that was revoking the family - "nothing happens").
    return sharedRefresh();
  }

  async request<T>(method: HttpMethod, endpoint: string, options: RequestOptions = {}): Promise<T> {
    const startTime = Date.now();
    const url = `${API_BASE}${endpoint}`;
    const fetchOptions = this.buildFetchOptions(method, options);

    let response = await fetch(url, fetchOptions);

    // Auto-refresh on 401 (skip for auth endpoints to avoid loops)
    if (response.status === 401 && !endpoint.startsWith('/auth/')) {
      const refreshed = await this.tryRefresh();
      if (refreshed) {
        const retryOptions = this.buildFetchOptions(method, options);
        response = await fetch(url, retryOptions);
      }
    }

    // Defensive: if anything in the chain (e.g. the Vite dev proxy or an
    // intermediary) still injects validators and yields a 304, force one
    // unconditional refetch so we get a real 200 + body instead of throwing.
    if (response.status === 304) {
      response = await fetch(url, { ...this.buildFetchOptions(method, options), cache: 'reload' });
    }

    const duration = Date.now() - startTime;
    const requestId = response.headers.get('x-request-id') || undefined;

    if (!response.ok) {
      let errorMessage = response.statusText;
      let details: unknown;

      try {
        const errorData = await response.json();
        errorMessage = errorData.message || errorMessage;
        details = errorData;
      } catch {
        // Response body is not JSON
      }

      const error = new ApiError(errorMessage, response.status, response.statusText, requestId, details);
      logger.api(method, endpoint, response.status, duration, error);
      throw error;
    }

    logger.api(method, endpoint, response.status, duration);

    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  get<T>(endpoint: string): Promise<T> {
    return this.request<T>('GET', endpoint);
  }

  post<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>('POST', endpoint, { body });
  }

  postForm<T>(endpoint: string, formData: FormData): Promise<T> {
    return this.request<T>('POST', endpoint, { formData });
  }

  put<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>('PUT', endpoint, { body });
  }

  patch<T>(endpoint: string, body?: unknown): Promise<T> {
    return this.request<T>('PATCH', endpoint, { body });
  }

  delete<T = void>(endpoint: string): Promise<T> {
    return this.request<T>('DELETE', endpoint);
  }
}

const http = new HttpClient();

/**
 * Authenticated fetch wrapper with automatic token refresh.
 * Use this instead of raw fetch() for API calls that need streaming
 * or other non-JSON responses (SSE, etc.) where HttpClient can't be used.
 */
export async function fetchWithAuth(url: string, options?: RequestInit): Promise<Response> {
  const headers = new Headers(options?.headers);
  if (!headers.has('X-Request-Id')) {
    headers.set('X-Request-Id', createRequestId());
  }
  // Wedge bridge: attach Bearer when a stored access token exists (no-op for
  // the CRM, which never stores one - cookie auth unchanged).
  const applyWedgeBearer = () => {
    const at = lsGet(AT_KEY);
    if (at) headers.set('Authorization', `Bearer ${at}`);
  };
  applyWedgeBearer();
  const opts: RequestInit = { ...options, headers, credentials: 'include' };
  let response = await fetch(url, opts);

  if (response.status === 401) {
    const refreshed = await sharedRefresh();
    if (refreshed) {
      applyWedgeBearer(); // carry the rotated access token on the retry
      response = await fetch(url, opts);
    }
  }

  return response;
}

export function createRequestId(): string {
  return crypto.randomUUID().split('-')[0];
}

export async function tracedFetch(
  url: string,
  options?: RequestInit,
): Promise<{ response: Response; requestId: string }> {
  const requestId = createRequestId();
  const headers = new Headers(options?.headers);
  headers.set('X-Request-Id', requestId);

  const response = await fetchWithAuth(url, { ...options, headers });
  return { response, requestId };
}

export function formatTraceError(message: string, requestId: string): string {
  return `${message} (ref: ${requestId})`;
}

export { API_BASE };

export interface VoiceActivityResponse {
  activity: AccountActivity;
  extractedData: {
    activityType: string;
    priority: string;
    topic: string;
    comments: string;
    sentiment: string;
    actionItems: string[];
    confidence: number;
  };
  transcript: string;
}

export interface CreateActivityInput {
  activityType: string;
  priority: string;
  status: string;
  topic?: string;
  comments?: string;
  activityDate?: string;
}

export const accounts = {
  list: () => http.get<Account[]>('/accounts'),
  search: (params: Record<string, string>) => {
    const query = new URLSearchParams(params).toString();
    return http.get<{ accounts: Account[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>(`/accounts/search?${query}`);
  },
  get: (id: string) => http.get<Account>(`/accounts/${id}`),
  create: (data: Partial<Account>) => http.post<Account>('/accounts', data),
  update: (id: string, data: Partial<Account>) => http.put<Account>(`/accounts/${id}`, data),
  delete: (id: string) => http.delete(`/accounts/${id}`),
  bulkDelete: (ids: string[]) =>
    http.post<{ deleted: number }>('/accounts/bulk-delete', { ids }),
  bulkRestore: (ids: string[]) =>
    http.post<{ restored: number }>('/accounts/bulk-restore', { ids }),
  merge: (primaryId: string, mergeIds: string[]) =>
    http.post<Account>('/accounts/merge', { primaryId, mergeIds }),
  // Live Anthropic web-research pass for a known account. Persists onto
  // researchData and returns the fresh result. Used by the PREP wedge scan.
  refreshIntel: (id: string) =>
    http.post<{ researchData: ResearchResult | null; query: string; location: string }>(
      `/accounts/${id}/research`,
      {},
    ),
  // Free-text research (query + optional location), no account row required.
  research: (query: string, location = '') =>
    http.post<ResearchResult | null>('/accounts/research', { query, location }),
  // Business-card vision: multipart image -> extracted business identity.
  // Goes through HttpClient.postForm so it carries the wedge Bearer token
  // (iOS-standalone bridge) automatically.
  researchCard: (form: FormData) =>
    http.postForm<{
      name: string; city: string; state: string; contactName: string;
      title: string; phone: string; email: string; website: string;
    }>('/accounts/research/card', form),
};

// Integrations (Google calendar link). EVERY call goes through HttpClient so
// it carries the wedge Bearer token bridge automatically (the prior raw
// `fetch(..., {credentials:'include'})` 401'd silently on iOS-standalone
// because the httpOnly cookie is dropped across PWA relaunch). credentials
// stay 'include' inside HttpClient so the OAuth CSRF state cookie still flows.
// Shape of the backend's CalendarEvent (apps/api .../calendar-adapter.interface.ts)
// as it comes over JSON; start/end are ISO strings on the wire, not Date.
export interface CalendarEventWire {
  id: string;
  title: string;
  start: string;
  end: string;
  attendees?: string[];
  location?: string;
  description?: string;
  meetingLink?: string;
  htmlLink?: string;
  // Which linked account this event came from (2026-09-28 audit fix #1):
  // the calendar service stamps it, since neither provider's own payload
  // says so. The Plan calendar's guest-facing move/cancel confirm reads
  // this to know Outlook can't suppress guest notifications.
  provider?: 'google' | 'microsoft';
  // True when a retried create returned the event an earlier attempt made
  // (idempotencyKey replay): edits on the retry were not applied and no new
  // invite went out.
  replayed?: boolean;
}

export interface CreateCalendarEventBody {
  title: string;
  start: string;
  end: string;
  timeZone?: string;
  attendees?: string[];
  description?: string;
  location?: string;
  addMeetLink?: boolean;
  sendInvites?: boolean;
  // Stable per-meeting key (the local PlanEvent id) so a retried create
  // returns the event the first attempt made instead of booking a second.
  idempotencyKey?: string;
}

export type UpdateCalendarEventBody = Partial<Omit<CreateCalendarEventBody, 'idempotencyKey'>>;

export const integrations = {
  linked: () =>
    http.get<Array<{ provider: string; scopes: string[]; status: string }>>(
      '/integrations/linked',
    ),
  calendarEvents: (hoursAhead = 48) =>
    http.get<{ events?: unknown[] } | unknown[]>(
      `/integrations/calendar/events?hoursAhead=${hoursAhead}`,
    ),
  // Explicit window for the calendar tab (Day/3 Day/Week/Month need whatever
  // is on screen, not "next N hours"). Same {linked, events, reason?} shape
  // as calendarEvents; the caller reuses one response handler for both.
  // truncated:true (2026-09-28 audit fix #2) means the backend's pagination
  // cap was hit for this range (a very heavily booked calendar); omitted
  // otherwise, so existing callers that don't check for it see no change.
  calendarEventsRange: (fromIso: string, toIso: string) =>
    http.get<{ linked: boolean; events: CalendarEventWire[]; reason?: string; truncated?: boolean }>(
      `/integrations/calendar/events?from=${encodeURIComponent(fromIso)}&to=${encodeURIComponent(toIso)}`,
    ),
  // Creates a real event on the rep's linked Google/Microsoft calendar. A
  // 403 { error: 'calendar_write_scope_missing' } means the linked account
  // needs a Calendar-write reconnect (read-only link, or a legacy iOS link
  // from before calendar.events existed) so the caller shows a reconnect CTA
  // rather than a generic error.
  createCalendarEvent: (body: CreateCalendarEventBody) =>
    http.post<CalendarEventWire>('/integrations/calendar/events', body),
  updateCalendarEvent: (id: string, body: UpdateCalendarEventBody) =>
    http.patch<CalendarEventWire>(`/integrations/calendar/events/${encodeURIComponent(id)}`, body),
  // sendInvites:false asks the provider to skip the cancellation email
  // (Google honors it; Outlook/Graph has no equivalent and always notifies,
  // see the backend adapter). Omitted -> notify (today's behavior).
  deleteCalendarEvent: (id: string, sendInvites?: boolean) =>
    http.delete<void>(
      `/integrations/calendar/events/${encodeURIComponent(id)}` + (sendInvites === false ? '?sendInvites=false' : ''),
    ),
  // GET returns { url, state } AND sets the httpOnly CSRF state cookie.
  // NOTE: the :provider route segment is case-SENSITIVE - OAuthService
  // .getProvider does a raw Map.get against the 'GOOGLE'|'MICROSOFT' keys
  // with no normalization; lowercase 'google' → 400 "Unknown OAuth provider".
  oauthStart: (redirectUri: string, scopeSet?: string) =>
    http.get<{ url: string; state: string }>(
      `/integrations/oauth/GOOGLE/start?redirectUri=${encodeURIComponent(redirectUri)}` +
        (scopeSet ? `&scopeSet=${encodeURIComponent(scopeSet)}` : ''),
    ),
  // POST { code, state, redirectUri }; backend validates state vs the cookie.
  oauthCallback: (body: { code: string; state: string; redirectUri: string }) =>
    http.post<{ ok?: boolean; provider?: string }>(
      '/integrations/oauth/GOOGLE/callback',
      body,
    ),
  // Create a real Gmail draft. Through HttpClient → carries the wedge Bearer
  // (the prior raw cookie fetch 401'd silently on iOS-standalone, so the rep
  // never got a real draft - only the compose-URL fallback).
  emailDraft: (body: { to: string; subject: string; html: string; plain: string }) =>
    http.post<{ id?: string; messageId?: string; draftId?: string }>(
      '/integrations/email/draft',
      body,
    ),
};

// Wedge product-surface endpoints (apps/api/src-nest/modules/wedge/*). All
// calls carry the wedge Bearer token bridge via HttpClient. Separate export
// group so the wedge has its own API namespace - see
// memory/feedback-wedge-standalone.md.
/** One candidate business from the pre-scrape disambiguation pass. */
export interface WedgeCandidate {
  name: string;
  address: string;
  city: string;
  state: string;
  phone: string | null;
  category: string | null;
  distinguisher: string;
  confidence: 'high' | 'medium' | 'low';
  // 2026-06-23 (WEDGE_ACCOUNT_RESOLVE_V1) · additive, optional resolution
  // fields the backend now attaches so the rep can confirm the right
  // business before a paid pull. All optional - render gracefully if absent.
  placeId?: string;
  // 2026-07-21 (route-real-coords) · Places-verified centroid so a confirmed
  // account routes on its real lat/lng instead of a town-center geocode.
  lat?: number | null;
  lng?: number | null;
  kind?: 'on_premise' | 'off_premise' | 'distributor' | 'corporate' | 'residential' | 'other';
  nonTarget?: boolean;
  closed?: boolean;
  distanceMi?: number | null;
  alreadySaved?: boolean;
  // 2026-08-21 · real Google signals · populated only on the ZIP-discover path.
  // (phone already exists above · shared with the resolve path.)
  rating?: number | null;
  reviewCount?: number | null;
  priceLevel?: string | null;
  openNow?: boolean | null;
  website?: string | null;
}

// 2026-06-25 (WEDGE_PLACES_DETAILS_V1) · verified Google ground-truth for a
// confirmed account, returned by GET /wedge/places/:placeId/details.
export interface WedgePlaceDetails {
  placeId: string;
  name: string | null;
  phone: string | null;
  website: string | null;
  businessStatus: 'operational' | 'closed_temporarily' | 'closed_permanently' | 'unknown';
  openNow: boolean | null;
  todayHours: string | null;
  weekdayHours: string[] | null;
  rating: number | null;
  reviewCount: number | null;
  priceLevel: string | null;
  // 2026-07-21 (route anchor confirm) · Google's own coordinate + postal
  // address for the confirmed place, returned on the SAME details call (they
  // are Essentials-tier fields on an already-Enterprise mask, so no extra
  // cost, no extra request). Optional so an older backend still type-checks;
  // null whenever Google omitted them - never fabricated.
  lat?: number | null;
  lng?: number | null;
  formattedAddress?: string | null;
  // 2026-07-27 · Google's own canonical Maps deep link plus the Open Location
  // Code for the confirmed place, returned on the SAME details call
  // (googleMapsUri is a Pro-tier field, plusCode an Essentials-tier field, and
  // the mask is already Enterprise, so no extra cost and no extra request).
  // Optional so an older backend still type-checks; null whenever Google
  // omitted them · never fabricated.
  googleMapsUri?: string | null;
  plusCode?: { globalCode: string | null; compoundCode: string | null } | null;
  fetchedAt: string;
}
export interface WedgePlaceDetailsResult {
  available: boolean;
  details: WedgePlaceDetails | null;
  cached: boolean;
}

// 2026-06-25 (WEDGE_PLACES_AUTOCOMPLETE_V1) · live type-ahead suggestions
// (each with a place_id), returned by POST /wedge/places/autocomplete.
export interface WedgeAutocompleteSuggestion {
  placeId: string;
  mainText: string;
  secondaryText: string;
  fullText: string;
  types: string[];
}
export interface WedgeAutocompleteResult {
  available: boolean;
  suggestions: WedgeAutocompleteSuggestion[];
}

// 2026-06-25 (Street View Static · WEDGE_STREETVIEW_V1) · a verified storefront
// image for a confirmed account. The backend probes the FREE metadata endpoint
// first (so a missing panorama costs nothing), stamps the capture date, and
// returns a backend PROXY url for the bytes (the Google key never reaches the
// client). imageUrl is non-null only when status === 'OK'.
// 2026-07-27 · the Pitch+ tier gate is gone (tier flatten · same functionality
// on every tier, quota-only differentiation). available:false now means only
// flag off or no key, never "your tier is too low".
export interface WedgeStreetViewResult {
  available: boolean;
  status: 'OK' | 'ZERO_RESULTS' | 'NOT_FOUND' | 'unavailable';
  imageUrl: string | null;
  captureDate: string | null;
}

// Property Intelligence (WEDGE_PROPERTY_INTEL_V1) · address-anchored parcel /
// owner / value / permit record + per-vertical fit score for the D2D verticals.
export interface WedgePropertyPermit {
  type: string | null;
  status: string | null;
  fileDate: string | null;
  issueDate: string | null;
  description: string | null;
}
export interface WedgePropertyRecord {
  available: boolean;
  status: 'ok' | 'not_found' | 'unavailable';
  formattedAddress: string | null;
  propertyType: string | null;
  yearBuilt: number | null;
  squareFootage: number | null;
  lotSizeSqft: number | null;
  bedrooms: number | null;
  bathrooms: number | null;
  ownerNames: string[] | null;
  ownerOccupied: boolean | null;
  lastSaleDate: string | null;
  lastSalePrice: number | null;
  assessedValue: number | null;
  estimatedValue: number | null;
  valueRange: { low: number | null; high: number | null };
  roofType: string | null;
  pool: boolean | null;
  permits: WedgePropertyPermit[];
  pending: string[];
  sources: { rentcast: boolean; shovels: boolean; realie: boolean };
}
export interface WedgePropertyFit {
  vertical: string;
  mode: string;
  fitScore: number | null;
  band: string;
  suppressed: boolean;
  suppressionReason: string | null;
  hero: { label: string; value: string } | null;
  factors: Array<{ label: string; detail: string; direction: string }>;
  pendingSignals: string[];
}
export interface WedgePropertyIntelResult {
  record: WedgePropertyRecord | null;
  fit: WedgePropertyFit | null;
}

// Instagram metrics for one handle (WEDGE ig-metrics · lazy, on brief DM-sheet /
// business-panel open). Every field is grounded-or-null · engagementRatePct is a
// derived estimate labeled "est." in the UI, never surfaced without real counts.
export interface WedgeIgMetrics {
  handle: string;
  followers: number | null;
  engagementRatePct: number | null;
  postsLast30d: number | null;
  lastPostAt: string | null;
}

// 2026-08-12 (T1 data-enrichment tiles · one per WEDGE_ENRICH_*_V1 source) ·
// the generic shape every enrichment tile returns. `available` is false only
// when the tile is fully off (flag off / no key / blank input); otherwise each
// per-source field is an { status, value, asOf } three-state value. The FE
// renders fields generically, so this stays intentionally open-ended.
export interface WedgeEnrichmentField {
  status: 'ok' | 'confirmed_none' | 'unknown' | 'not_queried';
  value: unknown;
  asOf: string | null;
}
export interface WedgeEnrichmentTile {
  source: string;
  available: boolean;
  status: 'ok' | 'not_found' | 'unavailable';
  asOf: string | null;
  [field: string]: unknown;
}

// 2026-06-25 (Drive export · WEDGE_DRIVE_EXPORT_V1, free, EXPORT-ONLY) · the
// result of dropping a rendered brief into the rep's own Google Drive. `error`
// is a typed reason the FE renders as a clean message ('drive_scope_missing' =
// "Drive access not enabled yet").
export interface WedgeDriveExportResult {
  ok: boolean;
  driveFileId: string | null;
  webViewLink: string | null;
  error: 'disabled' | 'google_not_linked' | 'drive_scope_missing' | 'upload_failed' | null;
}

// 2026-07-20 (Route refine · gated by WEDGE_ROUTES_PROXY_V1) · real traffic-aware
// drive-time for a proposed order via the box-IP Google Routes proxy. The FE
// prints minutes ONLY when provenance === 'live' (a real Google response);
// everything else falls back to the free straight-line distance. optimizedOrder
// (stop ids) is populated only when nothing is booked.
export interface WedgeRouteRefineResult {
  ok: boolean;
  reason: string | null;
  provenance: 'live' | 'estimated';
  totalSeconds: number | null;
  totalMeters: number | null;
  optimizedOrder: string[] | null;
  // stop id -> Google-resolved coordinate (an address-only stop geocoded inside
  // the one paid Routes call). Only ever populated on a 'live' response.
  geocoded?: Array<{ id: string; lat: number; lng: number }>;
  // per-leg drive time in seconds, in the VISITED order (leg i = travel from
  // visited stop i to visited stop i+1), from the SAME paid Routes call.
  // legFromId[i] / legToId[i] are the stop ids for leg i, so a leg maps to its
  // pair of stops without re-deriving the order. Only ever populated on a
  // 'live' response; empty on any parse gap, so minutes render ONLY when
  // provenance === 'live'.
  legSeconds?: number[];
  legFromId?: string[];
  legToId?: string[];
  message?: string;
}

export interface WedgeRouteStopInput {
  id?: string;
  placeId?: string | null;
  // lat/lng optional: an address-only stop is routed by `address` (Google
  // geocodes it inside the one paid call).
  lat?: number;
  lng?: number;
  address?: string | null;
  apptStart?: number;
}

export const wedge = {
  // Persist a fresh wedge scrape result onto an account's researchData
  // (only-known-accounts path). Called after streamWedgeResearch succeeds
  // so subsequent taps read from cache instead of re-scraping.
  persistResearch: (accountId: string, researchData: unknown) =>
    http.post<{ ok: boolean }>(`/wedge/research/persist/${accountId}`, { researchData }),
  // Cheap candidate disambiguation. Returns up to ~6 candidate businesses so
  // the rep confirms WHICH one before a research credit is spent. Does not
  // burn a search. Backend: POST /wedge/research/disambiguate.
  disambiguate: (query: string) =>
    http.post<{ query: string; candidates: WedgeCandidate[] }>('/wedge/research/disambiguate', { query }),
  // 2026-06-25 (Places Details · WEDGE_PLACES_DETAILS_V1) · verified Google
  // ground-truth (open-now + hours, tap-to-call phone, website, live rating,
  // price level) for an account the rep ALREADY confirmed, keyed by its
  // captured place_id. On-demand, 24h-cached server-side, on a controller
  // physically separate from the paid research SSE so it can never regress it.
  // Returns { available:false } when the flag is off / no key - FE shows nothing.
  placesDetails: (placeId: string) =>
    http.get<WedgePlaceDetailsResult>(`/wedge/places/${encodeURIComponent(placeId)}/details`),
  // 2026-06-25 (Places Autocomplete · WEDGE_PLACES_AUTOCOMPLETE_V1) · live
  // type-ahead account suggestions as the rep types. Session token groups the
  // keystrokes into one billable session; a pick carries the place_id straight
  // into the confirmed pull, skipping the $32/1k Text Search disambiguate.
  placesAutocomplete: (input: string, sessionToken?: string, territory?: { city?: string; state?: string; zip?: string }) =>
    http.post<WedgeAutocompleteResult>('/wedge/places/autocomplete', { input, sessionToken, territory }),
  // 2026-06-25 (Street View Static · WEDGE_STREETVIEW_V1) · metadata-stamped
  // storefront image for a confirmed account, keyed by its captured place_id
  // (or lat/lng fallback). The returned imageUrl is a backend proxy (no key).
  // Returns { available:false } when the flag is off or no key is configured ·
  // FE shows nothing.
  // 2026-07-27 · the Pitch+ tier gate is gone (tier flatten · same
  // functionality on every tier, quota-only differentiation).
  streetView: (params: { accountId: string; placeId?: string; lat?: number; lng?: number }) => {
    const q = new URLSearchParams();
    q.set('accountId', String(params.accountId || ''));
    if (params.placeId) q.set('placeId', String(params.placeId));
    else if (params.lat != null && params.lng != null) {
      q.set('lat', String(params.lat));
      q.set('lng', String(params.lng));
    }
    return http.get<WedgeStreetViewResult>(`/wedge/streetview?${q.toString()}`);
  },
  // Property Intelligence · GET the deterministic parcel/owner/value/permit
  // record + per-vertical fit score for an address (WEDGE_PROPERTY_INTEL_V1).
  propertyIntel: (params: {
    address: string;
    vertical: string;
    city?: string;
    state?: string;
    zip?: string;
    lat?: number;
    lng?: number;
  }) => {
    const q = new URLSearchParams();
    q.set('address', String(params.address || ''));
    q.set('vertical', String(params.vertical || ''));
    // Stated territory · lets the backend qualify the geocode and reject a
    // wrong-city (same-named-street) resolution instead of attaching it.
    if (params.city != null && String(params.city).trim()) q.set('city', String(params.city).trim());
    if (params.state != null && String(params.state).trim()) q.set('state', String(params.state).trim());
    if (params.zip != null && String(params.zip).trim()) q.set('zip', String(params.zip).trim());
    if (params.lat != null) q.set('lat', String(params.lat));
    if (params.lng != null) q.set('lng', String(params.lng));
    return http.get<WedgePropertyIntelResult>(`/wedge/property/intel?${q.toString()}`);
  },
  // Territory zone smart-search (WEDGE_TERRITORY_ZONES_V1) · resolve county /
  // state / town / ZIP suggestions for the "add a zone" typeahead. Backend uses
  // the Census geocoder + a cached county list. Empty when the flag is off.
  territorySuggest: (q: string) => {
    const p = new URLSearchParams();
    p.set('q', String(q || ''));
    return http.get<{ suggestions: Array<{ kind: string; label: string; state?: string; fips?: string; lat?: number; lng?: number }> }>(`/wedge/territory/suggest?${p.toString()}`);
  },
  // T1 enrichment tiles (WEDGE_ENRICH_*_V1) · GET one tile by source id, with
  // arbitrary string query params (address, name, state, npi, ...). Blank
  // params are dropped. The source is a fixed id from the FE plan, never user
  // input, so the path segment is safe.
  enrich: (source: string, params: Record<string, string | undefined>) => {
    const q = new URLSearchParams();
    for (const k of Object.keys(params)) {
      const v = params[k];
      if (v != null && String(v).trim()) q.set(k, String(v).trim());
    }
    return http.get<WedgeEnrichmentTile>(`/wedge/enrich/${encodeURIComponent(source)}?${q.toString()}`);
  },
  // Instagram metrics by handle · lazy, fetched when a rep opens a DM sheet or
  // the business panel (never during research). Paid (ScrapeCreators), cached
  // per handle server-side. Honest-null envelope on no-key / bad-handle / error.
  igMetrics: (handle: string) => {
    const q = new URLSearchParams();
    q.set('handle', String(handle || '').trim());
    return http.get<WedgeIgMetrics>(`/wedge/enrich/ig-metrics?${q.toString()}`);
  },
  // 2026-06-25 (Drive export · WEDGE_DRIVE_EXPORT_V1, free, EXPORT-ONLY) ·
  // render the supplied brief to HTML and drop it in the rep's Google Drive
  // (reuses the existing Google OAuth token + drive.file scope). $0 per call.
  driveExport: (payload: {
    accountId: string;
    name?: string; sector?: string; city?: string;
    summary?: string; keyInsight?: string; theTake?: string;
    website?: string; phone?: string; address?: string;
    moves?: string[]; signals?: string[];
  }) => http.post<WedgeDriveExportResult>('/wedge/drive/export', payload),
  // 2026-07-20 (Route refine · gated by WEDGE_ROUTES_PROXY_V1) · real drive-time
  // for the day's ordered stops. Server double-gated (flag + key) and metered;
  // with either one missing it returns provenance 'estimated'/'disabled', so the
  // FE simply keeps its free straight-line distance.
  routeRefine: (stops: WedgeRouteStopInput[]) =>
    http.post<WedgeRouteRefineResult>('/wedge/route/refine', { stops }),
  // 2026-06-10 (Background Resilience Wave 1) · recovery primitives.
  // /status returns {state:'in_flight'|'ready'|'idle', completedAt?, cacheAgeSeconds?, inFlightSinceMs?}.
  // /artifact returns {result, completedAt?, cacheAgeSeconds?} when cache hit.
  // FE bridge polls these on bridge mount + document.visibilitychange so a
  // rep returning from a backgrounded brief hydrates COMPANIES[id] from cache
  // without burning a fresh Anthropic search. Behind WEDGE_RECOVERY_V1 env
  // kill switch on the backend.
  researchStatus: (q: string, loc: string) =>
    http.get<{ state: 'in_flight' | 'ready' | 'idle'; completedAt?: string; cacheAgeSeconds?: number; inFlightSinceMs?: number; intent?: { q: string; loc: string; startedAt: number } }>(
      `/wedge/research/status?q=${encodeURIComponent(q)}&loc=${encodeURIComponent(loc)}`,
    ),
  researchArtifact: (q: string, loc: string) =>
    http.get<{ result: unknown | null; completedAt?: string; cacheAgeSeconds?: number }>(
      `/wedge/research/artifact?q=${encodeURIComponent(q)}&loc=${encodeURIComponent(loc)}`,
    ),
  // 2026-09-25 (async refine, WEDGE_BRIEF_REFINE_V1) · status of the background
  // refine a complete chunk announced with meta.refinePending + meta.refineId.
  // Owner-scoped server-side; 'applied' carries the refined brief.
  researchRefineStatus: (refineId: string) =>
    http.get<{ status: 'pending' | 'applied' | 'fallback'; result?: unknown; verification?: unknown }>(
      `/wedge/research/refine/${encodeURIComponent(refineId)}`,
    ),
  // 2026-09-24 (brief instant-open) · the rep's OWN newest frozen brief for an
  // account (WedgeBriefSnapshot) so a saved brief opens instantly without a
  // pull, plus the content-free per-account index the bridge uses to decide
  // "open from snapshot" and show "pulled N days ago". Read-only, never spends.
  briefSnapshotLatest: (name: string) =>
    http.get<
      | { found: false }
      | {
          found: true;
          id: string;
          accountName: string;
          city: string | null;
          zip: string | null;
          createdAt: string;
          content: unknown;
          source?: 'snapshot' | 'cache';
        }
    >(`/wedge/research/snapshot/latest?name=${encodeURIComponent(name)}`),
  briefSnapshotIndex: () =>
    http.get<
      Array<{
        id: string;
        accountKey: string;
        accountName: string;
        city: string | null;
        zip: string | null;
        createdAt: string;
        source?: 'snapshot' | 'cache';
      }>
    >(
      '/wedge/research/snapshot/index',
    ),
  // 2026-06-10 (Email + Test Wave) · self-verify push delivery. Settings
  // "Send test notification" button calls this. Returns {attempted,
  // succeeded, quietHoursSuppressed} so the FE can render "Sent to N of
  // M devices" feedback.
  pushTest: () =>
    http.post<{ attempted: number; succeeded: number; quietHoursSuppressed: boolean }>('/wedge/push/test', {}),
  // 2026-06-11 (Phase 1.1 · Anthropic outage banner) · cheap reader for
  // recent founder-tier alerts. Bridge polls on boot + every 5min,
  // Home renders an amber banner above MorningBriefing when active=true.
  outageState: () =>
    http.get<{ active: boolean; key: string | null; firedAt: number | null; message: string | null; ageMinutes: number | null }>(
      '/wedge/founder/outage-state',
    ),
  // 2026-06-11 (Phase 1.3 · Device-list management) · enumerate this
  // rep's subscribed devices for Settings > Notifications. Endpoint URL
  // is NEVER returned · use pushUnsubscribeById with the id field instead.
  pushSubscriptions: () =>
    http.get<{ items: Array<{ id: string; deviceClass: string; standaloneMode: boolean | null; swVersion: string | null; lastUsed: string | null; createdAt: string }> }>(
      '/wedge/push/subscriptions',
    ),
  pushUnsubscribeById: (id: string) =>
    http.delete<{ ok: boolean }>(`/wedge/push/subscribe/${encodeURIComponent(id)}`),
  // AI-generated personal pre-meeting email in the rep's industry voice.
  // Backend: POST /wedge/email/draft (reads wedge_prefs for the vertical pack).
  emailDraft: (payload: unknown) =>
    http.post<{ subject: string; body: string }>('/wedge/email/draft', payload),
  // Pocket Answers (2026-08-24 · passive-value law) · likely objections +
  // grounded counters for one account, generated downstream of a finished
  // brief. Backend: POST /wedge/pocket-answers.
  // Outreach tab (2026-09-28) · AI follow-up email or text for any Pipeline
  // lead, brief or no brief. Backend: POST /wedge/outreach/draft.
  outreachDraft: (payload: unknown) =>
    http.post<{ channel: 'email' | 'text'; subject: string; body: string }>('/wedge/outreach/draft', payload),
  pocketAnswers: (payload: unknown) =>
    http.post<{ answers: Array<{ objection: string; counter: string }> }>('/wedge/pocket-answers', payload),
  // Wedge "Saved briefs" - server-persisted (AIArtifact-backed, no migration).
  savedGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/saved'),
  savedSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/saved', { items }),
  // Recents/Live tab - server-persisted (AIArtifact 'wedge_recents', no
  // migration). Replace-whole-array contract, gated by WEDGE_RECENTS_V1.
  recentsGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/recents'),
  // 2026-08-04 (spend-confirm counter) · the rep's real monthly quota state.
  billingUsage: () =>
    http.get<{ tier: string; monthlyBriefCap: number; briefsUsed: number; bonusPullsRemaining?: number }>('/wedge/billing/usage'),
  recentsSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/recents', { items }),
  // Weekly planner · server-persisted (AIArtifact 'wedge_week_plan', no
  // migration). Single-element replace-whole-array contract; items[0] is the
  // { weekOf, days } plan object.
  weekPlanGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/week-plan'),
  weekPlanSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/week-plan', { items }),
  // Plan-tab calendar events · server-persisted (AIArtifact 'wedge_plan_events',
  // no migration). Replace-whole-array contract; items is the whole PlanEvent
  // array ({ id, account, town, kind, colorKey, start, durationMin, status,
  // source, createdAt, updatedAt, ... }). Client owns the item shape.
  loadPlanEvents: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/plan-events'),
  savePlanEvents: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/plan-events', { items }),
  // 2026-08-31 (Deal & ROI Builder, arsenal W2) · rep-built deal one-pagers
  // (AIArtifact 'wedge_deals', no migration). Replace-whole-array contract;
  // rows carry inputs only, derived numbers recomputed by wedgeDealMath.
  dealsGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/deals'),
  dealsSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/deals', { items }),
  // 2026-09-15 (Pipeline tab · the remade PREP surface) · the rep's personal
  // leads/contacts pipeline (AIArtifact 'wedge_pipeline', no migration).
  // Single-element replace-whole-array contract; items[0] is the pipeline blob
  // { v, revision, settings:{ stages }, leads:[...], deleted:[...] }.
  pipelineGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/pipeline'),
  pipelineSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/pipeline', { items }),
  // 2026-09-28 (Scout/BLIP intelligence upgrade · Teach Scout) · the rep's
  // custom knowledge store (AIArtifact 'wedge_knowledge', no migration).
  // Replace-whole-array contract; items are validated server-side (id, kind,
  // title, body, tags?, source, createdAt, updatedAt) and capped at 200
  // items / ~40k chars total - a 400 there means "trim before adding more".
  knowledgeGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/knowledge'),
  knowledgeSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/knowledge', { items }),
  // 2026-10-05 (Wedge Briefs upgrade) · rep-entered account contacts
  // (AIArtifact 'wedge_account_contacts', no migration). GET is one account's
  // rows (the caller's own, plus teammates' added values for an account
  // admin); POST is ONE edit, validated server-side (400 carries a
  // rep-readable message, 409 a duplicate) and answers with that account's
  // post-edit rows.
  accountContactsGet: (key: string, placeId?: string | null) =>
    http.get<{ entries: unknown[]; team: boolean }>(
      `/wedge/account-contacts?key=${encodeURIComponent(key)}${placeId ? `&placeId=${encodeURIComponent(placeId)}` : ''}`,
    ),
  accountContactsEdit: (op: Record<string, unknown>) =>
    http.post<{ entries: unknown[]; changedIds: string[] }>('/wedge/account-contacts', op),
  // 2026-10-05 (Wedge Briefs upgrade, phase 2) · one account's History feed,
  // newest first. `names` are the research queries its snapshots were stored
  // under (max 3); `before` pages back from the last row's time.
  accountHistoryGet: (key: string, o: { placeId?: string | null; names?: string[]; before?: string | null; limit?: number } = {}) => {
    const p = new URLSearchParams({ key });
    if (o.placeId) p.set('placeId', o.placeId);
    for (const n of (o.names || []).slice(0, 3)) if (n) p.append('name', n);
    if (o.before) p.set('before', o.before);
    if (o.limit) p.set('limit', String(o.limit));
    return http.get<{ items: unknown[]; nextBefore: string | null; team: boolean }>(`/wedge/account-history?${p.toString()}`);
  },
  // 2026-09-24 (Pipeline file import) · one file or pasted text to lead
  // rows for the review sheet (nothing is saved server-side). Multipart:
  // "file" and/or "text", plus a display "name". Rows follow the server's
  // ImportRow shape; table + mapping come back for spreadsheets so the
  // sheet can re-apply a column change locally.
  pipelineImportParse: (form: FormData) =>
    http.postForm<{
      rows: Array<Record<string, unknown>>;
      source: { kind: string; name: string; sheet?: string; sheets?: string[]; pages?: number };
      mapping?: Array<{ index: number; header: string; field: string; sample: string }>;
      table?: { headers: string[]; rows: string[][] };
      mappedBy?: 'rules' | 'ai';
      warnings: string[];
      truncated: boolean;
    }>('/wedge/pipeline/import/parse', form),
  // 2026-09-18 (Pipeline saved views) · the rep's named filter presets for the
  // Pipeline board (AIArtifact 'wedge_saved_views', no migration). Single-element
  // replace-whole-array contract; items[0] is the saved-views blob
  // { v, revision, views:[{ id, name, filters, createdAt }] }.
  savedViewsGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/saved-views'),
  savedViewsSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/saved-views', { items }),
  // 2026-09-18 (Last-visit identity spine · idea #1 + #9, dark behind
  // WEDGE_LAST_VISIT_V1) · the rep's per-account last-visit timestamps
  // (AIArtifact 'wedge_last_visit', no migration). Single-element
  // replace-whole-array contract; items[0] is the last-visit blob
  // { v, updatedAt, byKey:{ <acctKey>: <iso> } }. The FE only calls these when
  // the flag is on, so with it dark the routes are never exercised.
  lastVisitGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/last-visit'),
  lastVisitSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/last-visit', { items }),
  // 2026-06-11 (Rail Notepad) · per-account rep notes (AIArtifact
  // 'wedge_notes', no migration). Replace-whole-array contract.
  notesGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/notes'),
  notesSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/notes', { items }),
  // 2026-09-21 (Notes v2 journal, WEDGE_JOURNAL_V1) · the rep's dated
  // per-account entries plus a General bucket (AIArtifact 'wedge_journal',
  // no migration). Replace-whole-array contract. The SERVER derives the
  // legacy wedge_notes rollup from every journal save, so the client never
  // writes wedge_notes from the journal path.
  journalGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/journal'),
  journalSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/journal', { items }),
  // 2026-09-23 (Notes app · WEDGE_NOTES_APP_V1, dark) · the journal MERGE
  // contract. GET returns visits + per-account thread meta (non-deleted) and
  // the tombstoned ids; POST union-merges by id (greater updatedAt wins,
  // tombstones included) and echoes the ids it kept, dropped or truncated so
  // the device badge can be honest. Same 1MB envelope as /journal.
  journalSyncGet: () =>
    http.get<{ items: unknown[]; deletedIds: string[]; updatedAt: string | null }>('/wedge/journal/sync'),
  journalSyncPost: (items: unknown[], deletedIds: string[]) =>
    http.post<{ items: unknown[]; deletedIds: string[]; updatedAt: string | null; keptIds: string[]; droppedIds: string[]; truncatedIds: string[] }>('/wedge/journal/sync', { items, deletedIds }),
  // Shelf photo for a visit · multipart, rep-scoped, jpeg/png only. Returns
  // the served path (/public/uploads/notes/<user>/<file>) and its key.
  journalPhotoUpload: (form: FormData) =>
    http.postForm<{ key: string; url: string }>('/wedge/journal/photo', form),
  // VantageAI on notes (WEDGE_NOTES_AGENTS_V1, server kill switch) · voice
  // recap to structured fields. The server grounds every name, number and
  // date against the transcript and blanks anything it cannot find.
  journalStructure: (body: { transcript: string; today: string; weekday: string; accountName?: string }) =>
    http.post<{ who: string; what: string; next_step: string; follow_up_date: string | null; follow_up_what: string | null; status: 'won' | 'pushed' | 'follow' | 'risk' | null; tags: string[] }>('/wedge/journal/structure', body),
  // "Brief me" · SSE (delta / done / error). Streams text deltas to onDelta
  // and resolves with the server-validated three lines. Abort = Stop.
  journalBriefStream: async (
    body: { accountName: string; status?: string | null; followUp?: { date: string; what: string; done: boolean } | null; text: string },
    onDelta: (text: string) => void,
    signal?: AbortSignal,
  ): Promise<{ lines: string[]; thin: boolean }> => {
    const res = await fetchWithAuth(`${API_BASE}/wedge/journal/brief`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(body),
      signal,
    });
    if (!res.ok || !res.body) {
      let code = '';
      try { const j = await res.json(); code = String(j && (j.code || (j.error && j.error.code) || '')); } catch { /* not json */ }
      const err = new Error(res.status === 403 ? 'not_granted' : res.status === 429 ? 'rate_limited' : `brief_${res.status}`) as Error & { code?: string };
      err.code = code || (res.status === 403 ? 'not_granted' : res.status === 429 ? 'rate_limited' : 'failed');
      throw err;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let acc = '';
    let done: { lines: string[]; thin: boolean } | null = null;
    let failed: Error | null = null;
    const handle = (block: string) => {
      let event = 'message';
      let data = '';
      for (const line of block.split('\n')) {
        if (line.indexOf('event:') === 0) event = line.slice(6).trim();
        else if (line.indexOf('data:') === 0) data += line.slice(5).trim();
      }
      if (!data) return;
      let j: Record<string, unknown> = {};
      try { j = JSON.parse(data); } catch { return; }
      if (event === 'delta' && typeof j.text === 'string') { acc += j.text; onDelta(acc); }
      else if (event === 'done') {
        const lines = Array.isArray(j.lines) ? j.lines.filter((l): l is string => typeof l === 'string') : [];
        done = { lines, thin: j.thin === true };
      } else if (event === 'error') {
        const e = new Error(typeof j.message === 'string' ? j.message : 'brief failed') as Error & { code?: string };
        e.code = typeof j.code === 'string' ? j.code : 'failed';
        failed = e;
      }
    };
    for (;;) {
      const { value, done: end } = await reader.read();
      if (end) break;
      buf += decoder.decode(value, { stream: true });
      let idx = buf.indexOf('\n\n');
      while (idx !== -1) {
        handle(buf.slice(0, idx));
        buf = buf.slice(idx + 2);
        idx = buf.indexOf('\n\n');
      }
    }
    if (buf.trim()) handle(buf);
    if (failed) throw failed;
    if (done) return done;
    /* stream ended without a done event · hand back whatever streamed */
    const lines = acc.split('\n').map((l) => l.trim()).filter(Boolean).slice(0, 3);
    return { lines, thin: lines.length < 3 };
  },
  // 2026-09-21 (Notes tab · "Organize with AI", WEDGE_NOTES_AI_V1, dark) ·
  // cluster wedge_notes into themes + follow-ups (AIArtifact
  // 'wedge_notes_digest'). Cache-only read + a rep-triggered generate; the
  // FE only calls these when the flag is on, so with it dark the routes are
  // never exercised.
  notesDigestGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/notes/digest'),
  notesDigestGenerate: () =>
    http.post<{ items: unknown[]; updatedAt: string | null }>('/wedge/notes/digest', {}),
  // 2026-07-27 (b18 #12) · teammate brief handoff · same-org peers picker +
  // the one-brief copy into a teammate's Saved list. No CRM objects.
  teamPeersGet: () =>
    http.get<{ peers: Array<{ id: string; name: string; email: string }> }>('/wedge/team/peers'),
  savedHandoff: (opts: { toUserId: string; savedId: string; note?: string }) =>
    http.post<{ ok: boolean; reason: string | null }>('/wedge/saved/handoff', opts),
  // Server-first WedgeSignal persistence (P5) - intel signals / cross-surface
  // breadcrumbs, durable per-user (AIArtifact 'wedge_signal').
  signalsGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/signals'),
  signalsSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/signals', { items }),
  // §11 pre-warm linchpin - the device pushes its resolved upcoming account
  // ids so the overnight pre-warm worker has a target set.
  todayAccountsGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/today-accounts'),
  todayAccountsSave: (accountIds: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/today-accounts', { items: accountIds }),
  // Per-user wedge preferences (territory + industry) - server-persisted,
  // AIArtifact-backed ('wedge_prefs'). Stored as a single-element array.
  prefsGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/prefs'),
  prefsSave: (prefs: unknown) =>
    http.post<{ ok: boolean }>('/wedge/prefs', { items: [prefs] }),
  // Compounding loop · the rep's recent call outcomes, server-persisted
  // (AIArtifact 'wedge_outcomes'). The research + email prompts learn from it.
  outcomesGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/outcomes'),
  outcomesSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/outcomes', { items }),
  // Capture chain (2026-08-24) · in-app AI follow-up drafts generated after a
  // committed outcome (AIArtifact 'wedge_capture_drafts'). IN-APP ONLY · no
  // email delivery exists for this feature. Same replace-whole-array contract
  // as outcomes; the card flips read/dismissed via save.
  captureDraftsGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/followup-drafts'),
  captureDraftsSave: (items: unknown[]) =>
    http.post<{ ok: boolean }>('/wedge/followup-drafts', { items }),
  // RepXP server floor (2026-06-30) · never-degrade XP floor derived from the
  // rep's REAL server-tracked lifetime briefs. The leveling feature was
  // localStorage-only and reset on device switch / re-onboarding / state clear,
  // so a heavy rep could be stuck on Level 1. The client takes MAX(local XP,
  // this floor) so levels reflect real activity and survive wipes.
  repXpFloor: () =>
    http.get<{ floorXp: number; lifetimeBriefs: number; enabled: boolean }>('/wedge/billing/repxp'),
  // #4b (2026-05-25 post-launch) · tone-capture endpoint. Fires from the
  // email composer Send dispatch with {beforeText, afterText, intent}. The
  // backend persists to AIArtifact 'wedge_voice_profile' so future email
  // drafts learn the rep's actual edit pattern.
  feedbackCapture: (opts: { beforeText: string; afterText: string; intent: string }) =>
    http.post<{ ok: boolean }>('/wedge/feedback/capture', opts),
  // C2/S6 (2026-05-25 post-launch) · refinement suggestions mined from the
  // rep's saved + outcome briefs. Cache-first: the nightly S6 worker writes
  // suggestions into AIArtifact 'wedge_refinement_suggestions'; this endpoint
  // reads that cache. Empty array on a fresh rep / offline / no patterns yet.
  refinementSuggestionsGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/refinement/suggestions'),
  refinementSuggestionDismiss: (opts: { kind: string; value: string }) =>
    http.post<{ ok: boolean }>('/wedge/refinement/suggestions/dismiss', opts),
  // 2026-09-29 (Notifications v2) · server-backed, cross-device bell inbox.
  // GET returns this rep's feed (newest first) + unread count; the two
  // writes are scoped server-side to the caller, never a client-supplied id.
  notifFeedGet: () =>
    http.get<{ items: Array<Record<string, unknown>>; unread: number }>('/wedge/notifications'),
  notifFeedMarkRead: (target: { ids: string[] } | { all: 'all' }) =>
    http.post<{ count: number }>('/wedge/notifications/read', target),
  notifFeedDelete: (id: string) =>
    http.delete<{ ok: boolean }>(`/wedge/notifications/${encodeURIComponent(id)}`),
  // Tester suite (2026-05-25 post-launch) · internal-only endpoints used
  // by the @repvantage.io / WEDGE_TESTER_EMAILS / PAID_PREP_EMAILS tester
  // accounts to view-as any tier + reset specific testing-state slices
  // (onboarding, suggestions, voice profile, outcomes) without polluting
  // prod data via throwaway accounts.
  testerIdentity: () =>
    http.get<{ isInternal: boolean; email: string; realTier: string }>('/wedge/tester/identity'),
  testerReset: (opts: { kind: string }) =>
    http.post<{ ok: boolean; kind: string }>('/wedge/tester/reset', opts),
  // C3 (2026-05-25 post-launch) · weekly Friday recap. Returns the rep's
  // latest recap blob from the WedgeFridayRecapService cron.
  fridayRecapGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/friday-recap'),
  // C4 (2026-05-25 post-launch) · onboarding coach state · queued milestones
  // for day 1/3/7/14 of the rep's lifecycle.
  coachGet: () =>
    http.get<{ items: unknown[]; updatedAt: string | null }>('/wedge/coach'),
  coachDismiss: () =>
    http.post<{ ok: boolean }>('/wedge/coach/dismiss', {}),
  // --- Calendar share opt-in (2026-09-28) ---------------------------------
  // The rep's opt-in to share their calendar with their team admins. GET goes
  // through fetchWithAuth, not http, so a 404 (backend not deployed yet)
  // resolves to null quietly instead of logging an API warning; the bridge
  // reads null as "feature unavailable" and the toggle stays hidden.
  calendarShareGet: async (): Promise<{ share: boolean; updatedAt: string | null; teamAdmins: boolean } | null> => {
    const res = await fetchWithAuth(`${API_BASE}/wedge/calendar/share`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new ApiError(res.statusText || 'calendar_share_get_failed', res.status, res.statusText);
    const j = (await res.json()) as { share?: unknown; updatedAt?: unknown; teamAdmins?: unknown };
    return {
      share: j?.share === true,
      updatedAt: typeof j?.updatedAt === 'string' ? j.updatedAt : null,
      teamAdmins: j?.teamAdmins === true,
    };
  },
  calendarShareSet: (share: boolean) =>
    http.post<{ ok: boolean; share: boolean }>('/wedge/calendar/share', { share }),
  // --- end calendar share ---------------------------------------------------
  // Team admin (issue #5) · ships DARK behind WEDGE_ADMIN_TEAM_ENABLED.
  // capabilities is always safe to call (returns false/false when the gate
  // is off or the caller is not an org-admin parent). team + saved are 403
  // when unauthorized. All three are read-only.
  adminCapabilities: () =>
    http.get<{ teamAdmin?: boolean; teamEnabled?: boolean }>('/wedge/admin/capabilities'),
  // /team + /saved are documented to return a bare array; the bridge also
  // tolerates an { items: [...] } envelope, so type the response loosely.
  adminTeam: () =>
    http.get<unknown>('/wedge/admin/team'),
  adminSaved: (userId: string) =>
    http.get<unknown>(`/wedge/admin/saved?userId=${encodeURIComponent(userId)}`),
  // Account-admin dashboard (FinCo) · gated on WEDGE_ACCOUNT_ADMIN_ENABLED
  // (default off, ENABLED in prod as of 2026-07 · verify on the box, not here).
  // DOMAIN-scoped (not org-linking): aggregates briefs + engagement for every
  // user on the caller's own email domain. capabilities is always safe to call
  // (false/null when the gate is off or the caller is not allowlisted); summary
  // is 403 when unauthorized. Both read-only.
  accountAdminCapabilities: () =>
    http.get<{ accountAdmin?: boolean; accountAdminEnabled?: boolean; domain?: string | null }>(
      '/wedge/admin/account/capabilities',
    ),
  accountSummary: () =>
    http.get<unknown>('/wedge/admin/account/summary'),
  /* b120 · manager view v1: per-member outcome pulse (30d W/L/P, saved,
     last outcome) for the account-admin lane. */
  teamPulse: () =>
    http.get<unknown>('/wedge/admin/team-pulse'),
  // Per-rep drill-down for the account dashboard: onboarding, brief history,
  // email history, assignments, and metrics. 403 when unauthorized or the rep
  // is off the caller's domain. Read-only.
  accountMember: (userId: string) =>
    http.get<unknown>(`/wedge/admin/account/member?userId=${encodeURIComponent(userId)}`),
  // Heavier management analytics (onboarding funnel, activity feed, ZIP
  // coverage, account attribution, cap watch), loaded alongside the summary.
  // 403 when unauthorized. Read-only.
  accountInsights: () =>
    http.get<unknown>('/wedge/admin/account/insights'),
  // Rep's OWN 14-day daily brief counts · You-tab spark. Read-only, ctx-scoped.
  myBriefSeries: () =>
    http.get<unknown>('/wedge/research/my-series'),
  // 2026-07-17 · Account-admin assignment (same-domain, enforced server-side).
  // assign-manager sets/clears a rep's manager (managerUserId null unassigns);
  // assign-targets replaces a rep's book of business (named targets + owned
  // ZIPs); assignments reads it back. All 403 for a non-admin.
  // 2026-07-19 · Account-admin quota config (replace-whole, same-domain,
  // enforced server-side). Echoes the saved config.
  setAccountQuotas: (body: { teamMonthlyBriefs?: number | null; perRep?: Record<string, number> }) =>
    http.post<unknown>('/wedge/admin/account/set-quotas', body),
  assignManager: (body: { repUserId: string; managerUserId?: string | null }) =>
    http.post<unknown>('/wedge/admin/account/assign-manager', body),
  assignTargets: (body: {
    repUserId: string;
    targets?: Array<{ name: string; placeId?: string | null; zip?: string | null }>;
    zips?: string[];
  }) => http.post<unknown>('/wedge/admin/account/assign-targets', body),
  accountAssignments: (repUserId: string) =>
    http.get<unknown>(`/wedge/admin/account/assignments?repUserId=${encodeURIComponent(repUserId)}`),
  // 2026-07-20 · org membership: rep-side join request + admin-side pending
  // list and approve/decline (approval also triggers the sign-in email).
  orgJoinRequest: (domain: string) =>
    http.post<unknown>('/wedge/org/join-request', { domain }),
  accountJoinRequests: () =>
    http.get<unknown>('/wedge/admin/account/join-requests'),
  accountDecideMember: (body: { email: string; approve: boolean }) =>
    http.post<unknown>('/wedge/admin/account/decide-member', body),
  // 2026-07-20 · RepXP merch milestones (server-validated claims).
  repxpClaims: () => http.get<unknown>('/wedge/repxp/claims'),
  repxpClaim: (level: number) => http.post<unknown>('/wedge/repxp/claim', { level }),
  // 2026-06-23 · Batch Research Engine · gated on WEDGE_BATCH_ENABLED (default
  // off, ENABLED in prod as of 2026-07 · verify on the box, not here).
  // Queue many briefs at once; each lands with the standard cross-device push
  // + email "intel ready" notification. All three 404 when the flag is off
  // and 403 below the Pitch tier · the bridge tolerates both.
  // placeId is the optional confirmed-entity handle from the
  // WEDGE_ACCOUNT_RESOLVE_V1 pre-flight; additive and backward-compatible
  // (omitted when resolution is unavailable or the flag is off).
  batchCreate: (items: Array<{ query: string; location?: string; placeId?: string }>) =>
    http.post<unknown>('/wedge/research/batch', { items }),
  batchList: () => http.get<unknown>('/wedge/research/batch'),
  batchGet: (id: string) =>
    http.get<unknown>(`/wedge/research/batch/${encodeURIComponent(id)}`),
  // 2026-07-17 · Area Discovery · gated on WEDGE_DISCOVERY_ENABLED (404 when
  // off). Enumerate up to 20 real businesses for a query inside a ZIP; the rep
  // confirms the subset and feeds them into batchCreate. FINDS accounts; batch
  // RESEARCHES them.
  discover: (body: { query: string; zip: string; vertical?: string; limit?: number; country?: string }) =>
    http.post<unknown>('/wedge/research/discover', body),
};

export const activities = {
  list: (accountId: string) => http.get<AccountActivity[]>(`/accounts/${accountId}/activities`),
  create: (accountId: string, data: CreateActivityInput) =>
    http.post<AccountActivity>(`/accounts/${accountId}/activities`, data),
  createVoice: (accountId: string, audioBlob: Blob) => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');
    return http.postForm<VoiceActivityResponse>(`/accounts/${accountId}/activities`, formData);
  },
  delete: (accountId: string, activityId: string) =>
    http.delete(`/accounts/${accountId}/activities/${activityId}`),
};

export interface AgentEnvelope<T = unknown> {
  status: 'completed' | 'error' | string;
  parsed: T | null;
  error?: string;
  durationMs?: number;
  totalCostUsd?: number;
  model?: string;
  raw?: string;
}

export const agent = {
  call: <T = unknown>(slug: string, body: unknown): Promise<AgentEnvelope<T>> =>
    http.post<AgentEnvelope<T>>(`/agent/${slug}`, body),
};

export { ApiError as default };
